import { useState, useCallback } from "react";
import type { Project, SynthesisStep, Chemical, StepVariant } from "@/types/chemistry";
import { getNextDefaultName } from "@/lib/stepUtils";
import { createDemoProject, createMultiCompoundDemo } from "@/data/demoProject";

const STORAGE_KEY = "chem-route-projects";
const DEMO_SEEDED_KEY = "chem-route-demo-seeded";

const generateId = () => crypto.randomUUID();

const createEmptyChemical = (): Chemical => ({
  id: generateId(),
  name: "",
  formula: "",
  casNumber: "",
  earliestDate: "",
  price: 0,
  co2eq: 0,
  suppliers: "",
});

export const createEmptyVariant = (index: number): StepVariant => ({
  id: generateId(),
  label: `Variant ${String.fromCharCode(65 + index)}`,
  status: "planning",
  completed: false,
  earliestDelivery: "",
  earliestCompletion: "",
  productAmount: "",
  yield: "",
  stepTime: "",
  deadline: "",
  notes: "",
});

export const createEmptyStep = (parentId: string | null, name: string = ""): SynthesisStep => {
  const firstVariant = createEmptyVariant(0);
  return {
    id: generateId(),
    parentId,
    name,
    label: "Step",
    status: "planning",
    completed: false,
    earliestDelivery: "",
    earliestCompletion: "",
    productAmount: "",
    yield: "",
    stepTime: "",
    deadline: "",
    reactants: [],
    reagents: [],
    products: [],
    reactionString: "",
    notes: "",
    variants: [firstVariant],
    activeVariantId: firstVariant.id,
  };
};

// --- Tree utilities ---

/** Build children map from steps array */
function buildChildrenMap(steps: SynthesisStep[]): Record<string, SynthesisStep[]> {
  const map: Record<string, SynthesisStep[]> = {};
  steps.forEach(s => {
    if (s.parentId) {
      if (!map[s.parentId]) map[s.parentId] = [];
      map[s.parentId].push(s);
    }
  });
  return map;
}

/** Get all descendant IDs of a step */
function getDescendantIds(stepId: string, childrenMap: Record<string, SynthesisStep[]>): string[] {
  const result: string[] = [];
  const queue = [stepId];
  while (queue.length > 0) {
    const current = queue.pop()!;
    const children = childrenMap[current] || [];
    for (const child of children) {
      result.push(child.id);
      queue.push(child.id);
    }
  }
  return result;
}

/** Relabel steps for retrosynthesis layout.
 * The root (target compound, rightmost) gets the highest number.
 * Leaves (leftmost precursors) start at Step 1.
 * At forks: "Step 3a", "Step 3b", etc.
 */
function relabelSteps(steps: SynthesisStep[]): SynthesisStep[] {
  const childrenMap = buildChildrenMap(steps);
  const roots = steps.filter(s => !s.parentId);
  const labelMap = new Map<string, string>();

  // First pass: compute max depth from each node
  function getMaxDepth(step: SynthesisStep): number {
    const children = childrenMap[step.id] || [];
    if (children.length === 0) return 1;
    return 1 + Math.max(...children.map(getMaxDepth));
  }

  // Walk tree: root gets totalDepth, children get decreasing numbers
  function walk(step: SynthesisStep, myNumber: number, suffix: string) {
    labelMap.set(step.id, `Step ${myNumber}${suffix}`);
    const children = childrenMap[step.id] || [];
    if (children.length === 1) {
      walk(children[0], myNumber - 1, suffix);
    } else if (children.length > 1) {
      children.forEach((child, i) => {
        const newSuffix = String.fromCharCode(97 + i);
        walk(child, myNumber - 1, newSuffix);
      });
    }
  }

  if (roots.length === 1) {
    const totalDepth = getMaxDepth(roots[0]);
    walk(roots[0], totalDepth, "");
  } else {
    roots.forEach((root, i) => {
      const totalDepth = getMaxDepth(root);
      walk(root, totalDepth, String.fromCharCode(97 + i));
    });
  }

  return steps.map(s => ({ ...s, label: labelMap.get(s.id) || s.label }));
}

/** Migrate old steps that lack parentId */
function migrateSteps(steps: SynthesisStep[]): SynthesisStep[] {
  if (steps.length === 0) return steps;
  if (steps[0].parentId !== undefined && steps[0].parentId !== null) return steps;
  // Check if any step has parentId set
  if (steps.some(s => s.parentId !== undefined && s.parentId !== null)) return steps;
  // Assign linear chain
  return steps.map((s, i) => ({
    ...s,
    parentId: i === 0 ? null : steps[i - 1].id,
  }));
}

/** Known formula→name mapping for migration */
const FORMULA_NAME_MAP: Record<string, string> = {
  "C₆H₅COCH₃": "Acetophenone",
  "(CH₃CO)₂O": "Acetic anhydride",
  "AlCl₃": "Aluminium chloride",
  "C₁₀H₁₂O": "4-Isobutylacetophenone",
  "CH₃COCl": "Acetyl chloride",
  "FeCl₃": "Iron(III) chloride",
  "BF₃·Et₂O": "Boron trifluoride diethyl etherate",
  "NaBH₄": "Sodium borohydride",
  "C₁₀H₁₄O": "4-Isobutyl-α-methylbenzyl alcohol",
  "LiAlH₄": "Lithium aluminium hydride",
  "HCl": "Hydrochloric acid",
  "ZnCl₂": "Zinc chloride",
  "C₁₀H₁₃Cl": "1-Chloro-2-(4-isobutylphenyl)propane",
  "NaCN": "Sodium cyanide",
  "DMSO": "Dimethyl sulfoxide",
  "C₁₁H₁₃N": "2-(4-Isobutylphenyl)propionitrile",
  "KCN": "Potassium cyanide",
  "DMF": "Dimethylformamide",
  "TMSCN": "Trimethylsilyl cyanide",
  "TBAF": "Tetrabutylammonium fluoride",
  "Pd(PPh₃)₄": "Tetrakis(triphenylphosphine)palladium(0)",
  "Zn(CN)₂": "Zinc cyanide",
  "H₂SO₄": "Sulfuric acid",
  "H₂O": "Water",
  "C₁₁H₁₄O₂": "2-(4-Isobutylphenyl)propionic acid",
  "NaOH": "Sodium hydroxide",
  "H₂O₂": "Hydrogen peroxide",
  "SOCl₂": "Thionyl chloride",
  "CH₃OH": "Methanol",
  "C₁₂H₁₆O₂": "Methyl 2-(4-isobutylphenyl)propanoate",
  "LiOH": "Lithium hydroxide",
  "THF/H₂O": "THF/Water",
  "C₁₃H₁₈O₂": "Ibuprofen",
  "KOH": "Potassium hydroxide",
  "MeOH/H₂O": "Methanol/Water",
};

function migrateChemicalNames(chem: Chemical): Chemical {
  if (chem.name) return chem;
  const mapped = FORMULA_NAME_MAP[chem.formula];
  return mapped ? { ...chem, name: mapped } : chem;
}

function migrateStepNames(step: SynthesisStep): SynthesisStep {
  return {
    ...step,
    name: step.name ?? "",
    reactants: step.reactants.map(migrateChemicalNames),
    reagents: step.reagents.map(migrateChemicalNames),
    products: step.products.map(migrateChemicalNames),
    variants: (step.variants || []).map(v => ({
      ...v,
    })),
  };
}

const loadProjects = (): Project[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const projects = raw ? JSON.parse(raw) : [];
    if (!localStorage.getItem(DEMO_SEEDED_KEY)) {
      projects.push(createDemoProject());
      projects.push(createMultiCompoundDemo());
      localStorage.setItem(DEMO_SEEDED_KEY, "true");
    }
    if (!localStorage.getItem("chem-route-multi-demo-seeded")) {
      const hasMulti = projects.some((p: Project) => p.name === "Analgesic Compound Library");
      if (!hasMulti) {
        projects.push(createMultiCompoundDemo());
      }
      localStorage.setItem("chem-route-multi-demo-seeded", "true");
    }

    // Cleanup/migration
    const normalized = projects.map((p: Project) => {
      let steps = p.steps;
      steps = migrateSteps(steps);
      steps = steps.map(migrateStepNames);

      // Re-seed Ibuprofen demo if it was corrupted (only 1 step remaining)
      if (p.name === "Ibuprofen Total Synthesis" && steps.length <= 1) {
        const demo = createDemoProject();
        steps = demo.steps;
      }

      // Re-seed Analgesic demo if products lack names (stale seed)
      if (p.name === "Analgesic Compound Library") {
        const rootProducts = steps.filter(s => !s.parentId).flatMap(s => s.products);
        const hasNames = rootProducts.some(pr => pr.name && pr.name.length > 0);
        if (!hasNames) {
          const demo = createMultiCompoundDemo();
          steps = demo.steps;
        }
      }

      steps = relabelSteps(steps);
      return { ...p, steps };
    });

    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    return normalized;
  } catch {
    return [];
  }
};

const saveProjects = (projects: Project[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
};

export function useProjects() {
  const [projects, setProjects] = useState<Project[]>(loadProjects);

  const persist = useCallback((updated: Project[]) => {
    setProjects(updated);
    saveProjects(updated);
  }, []);

  const createProject = useCallback((name: string, targetCompound: string, description = "", deadline?: string) => {
    const initialStep = createEmptyStep(null, "Reaction A");
    const project: Project = {
      id: generateId(),
      name,
      description,
      status: "planning",
      lastModified: new Date().toISOString(),
      targetCompound,
      deadline,
      steps: [initialStep],
    };
    const withLabels = { ...project, steps: relabelSteps(project.steps) };
    persist([...projects, withLabels]);
    return withLabels;
  }, [projects, persist]);

  const updateProject = useCallback((id: string, updates: Partial<Project>) => {
    persist(projects.map(p => p.id === id ? { ...p, ...updates, lastModified: new Date().toISOString() } : p));
  }, [projects, persist]);

  const deleteProject = useCallback((id: string) => {
    persist(projects.filter(p => p.id !== id));
  }, [projects, persist]);

  const getProject = useCallback((id: string) => {
    return projects.find(p => p.id === id);
  }, [projects]);

  const updateStep = useCallback((projectId: string, stepId: string, updates: Partial<SynthesisStep>) => {
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      return {
        ...p,
        lastModified: new Date().toISOString(),
        steps: p.steps.map(s => s.id === stepId ? { ...s, ...updates } : s),
      };
    }));
  }, [projects, persist]);

  /** Add a child step to a specific step (extend a branch) */
  const addStepAfter = useCallback((projectId: string, parentStepId: string): string => {
    const proj = projects.find(p => p.id === projectId);
    const defaultName = getNextDefaultName(proj?.steps || []);
    const newStep = createEmptyStep(parentStepId, defaultName);
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      const newSteps = relabelSteps([...p.steps, newStep]);
      return { ...p, lastModified: new Date().toISOString(), steps: newSteps };
    }));
    return newStep.id;
  }, [projects, persist]);

  /** Add a new branch as a sibling (same parent as the step's siblings) */
  const addBranch = useCallback((projectId: string, parentId: string): string => {
    const proj = projects.find(p => p.id === projectId);
    const defaultName = getNextDefaultName(proj?.steps || []);
    const newStep = createEmptyStep(parentId, defaultName);
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      const newSteps = relabelSteps([...p.steps, newStep]);
      return { ...p, lastModified: new Date().toISOString(), steps: newSteps };
    }));
    return newStep.id;
  }, [projects, persist]);

  /** Legacy addStep: append to the first leaf found, or use afterIndex for linear compat */
  const addStep = useCallback((projectId: string, afterIndex?: number) => {
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      const defaultName = getNextDefaultName(p.steps);
      const childrenMap = buildChildrenMap(p.steps);
      const leaves = p.steps.filter(s => !(childrenMap[s.id]?.length));
      if (afterIndex !== undefined && afterIndex < p.steps.length) {
        const parentStep = p.steps[afterIndex];
        const newStep = createEmptyStep(parentStep.id, defaultName);
        const newSteps = relabelSteps([...p.steps, newStep]);
        return { ...p, lastModified: new Date().toISOString(), steps: newSteps };
      }
      const parentId = leaves.length > 0 ? leaves[0].id : null;
      const newStep = createEmptyStep(parentId, defaultName);
      const newSteps = relabelSteps([...p.steps, newStep]);
      return { ...p, lastModified: new Date().toISOString(), steps: newSteps };
    }));
  }, [projects, persist]);

  const deleteStep = useCallback((projectId: string, stepId: string) => {
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      const childrenMap = buildChildrenMap(p.steps);
      const descendantIds = getDescendantIds(stepId, childrenMap);
      const toRemove = new Set([stepId, ...descendantIds]);
      const remaining = relabelSteps(p.steps.filter(s => !toRemove.has(s.id)));
      return { ...p, lastModified: new Date().toISOString(), steps: remaining };
    }));
  }, [projects, persist]);

  const addChemical = useCallback((projectId: string, stepId: string, type: "reactants" | "reagents" | "products", chemical?: Partial<Chemical>) => {
    const newChem: Chemical = { ...createEmptyChemical(), ...chemical };
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      return {
        ...p,
        lastModified: new Date().toISOString(),
        steps: p.steps.map(s => {
          if (s.id !== stepId) return s;
          return { ...s, [type]: [...s[type], newChem] };
        }),
      };
    }));
  }, [projects, persist]);
  
  const updateChemical = useCallback((projectId: string, stepId: string, type: "reactants" | "reagents" | "products", chemicalId: string, updates: Partial<Chemical>) => {
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      return {
        ...p,
        lastModified: new Date().toISOString(),
        steps: p.steps.map(s => {
          if (s.id !== stepId) return s;
          return {
            ...s,
            [type]: s[type].map((c: Chemical) => c.id === chemicalId ? { ...c, ...updates } : c),
          };
        }),
      };
    }));
  }, [projects, persist]);

  const removeChemical = useCallback((projectId: string, stepId: string, type: "reactants" | "reagents" | "products", chemicalId: string) => {
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      return {
        ...p,
        lastModified: new Date().toISOString(),
        steps: p.steps.map(s => {
          if (s.id !== stepId) return s;
          return { ...s, [type]: s[type].filter((c: Chemical) => c.id !== chemicalId) };
        }),
      };
    }));
  }, [projects, persist]);

  const addVariant = useCallback((projectId: string, stepId: string) => {
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      return {
        ...p,
        lastModified: new Date().toISOString(),
        steps: p.steps.map(s => {
          if (s.id !== stepId) return s;
          const newVariant = createEmptyVariant(s.variants.length);
          return { ...s, variants: [...s.variants, newVariant] };
        }),
      };
    }));
  }, [projects, persist]);

  const updateVariant = useCallback((projectId: string, stepId: string, variantId: string, updates: Partial<StepVariant>) => {
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      return {
        ...p,
        lastModified: new Date().toISOString(),
        steps: p.steps.map(s => {
          if (s.id !== stepId) return s;
          return {
            ...s,
            variants: s.variants.map(v => v.id === variantId ? { ...v, ...updates } : v),
          };
        }),
      };
    }));
  }, [projects, persist]);

  const deleteVariant = useCallback((projectId: string, stepId: string, variantId: string) => {
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      return {
        ...p,
        lastModified: new Date().toISOString(),
        steps: p.steps.map(s => {
          if (s.id !== stepId) return s;
          if ((s.variants || []).length <= 1) return s;
          const remaining = (s.variants || []).filter(v => v.id !== variantId);
          return {
            ...s,
            variants: remaining,
            activeVariantId: s.activeVariantId === variantId ? remaining[0]?.id : s.activeVariantId,
          };
        }),
      };
    }));
  }, [projects, persist]);

  const setActiveVariant = useCallback((projectId: string, stepId: string, variantId: string | undefined) => {
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      return {
        ...p,
        lastModified: new Date().toISOString(),
        steps: p.steps.map(s => {
          if (s.id !== stepId) return s;
          return { ...s, activeVariantId: variantId };
        }),
      };
    }));
  }, [projects, persist]);

  /** Add a forward branch: insert intermediate fork node F between step and its parent, then add a new sibling */
  const addBranchForward = useCallback((projectId: string, stepId: string): { intermediateId: string; branchId: string } => {
    const proj = projects.find(p => p.id === projectId);
    const step = proj?.steps.find(s => s.id === stepId);
    const defaultName = getNextDefaultName(proj?.steps || []);
    const intermediate = createEmptyStep(step?.parentId ?? null, "");
    const newBranch = createEmptyStep(intermediate.id, defaultName);

    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      // Reparent step to intermediate
      const updatedSteps = p.steps.map(s => {
        if (s.id === stepId) return { ...s, parentId: intermediate.id };
        return s;
      });
      return { ...p, lastModified: new Date().toISOString(), steps: relabelSteps([...updatedSteps, intermediate, newBranch]) };
    }));
    return { intermediateId: intermediate.id, branchId: newBranch.id };
  }, [projects, persist]);

  /** Insert a new step between a child and its parent (forward direction) */
  const addStepBefore = useCallback((projectId: string, childStepId: string): string => {
    const proj = projects.find(p => p.id === projectId);
    const defaultName = getNextDefaultName(proj?.steps || []);
    const child = proj?.steps.find(s => s.id === childStepId);
    const newStep = createEmptyStep(child?.parentId ?? null, defaultName);
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      const updatedSteps = p.steps.map(s => {
        if (s.id === childStepId) return { ...s, parentId: newStep.id };
        return s;
      });
      return { ...p, lastModified: new Date().toISOString(), steps: relabelSteps([...updatedSteps, newStep]) };
    }));
    return newStep.id;
  }, [projects, persist]);


  /** Add a new root step after the current root (forward direction) */
  const addRootStep = useCallback((projectId: string): string => {
    const proj = projects.find(p => p.id === projectId);
    const defaultName = getNextDefaultName(proj?.steps || []);
    const newStep = createEmptyStep(null, defaultName);
    persist(projects.map(p => {
      if (p.id !== projectId) return p;
      const currentRoots = p.steps.filter(s => !s.parentId);
      const updatedSteps = p.steps.map(s => {
        if (!s.parentId) return { ...s, parentId: newStep.id };
        return s;
      });
      return { ...p, lastModified: new Date().toISOString(), steps: relabelSteps([...updatedSteps, newStep]) };
    }));
    return newStep.id;
  }, [projects, persist]);

  return {
    projects,
    createProject,
    updateProject,
    deleteProject,
    getProject,
    updateStep,
    addStep,
    addStepAfter,
    addBranch,
    deleteStep,
    addChemical,
    updateChemical,
    removeChemical,
    addVariant,
    updateVariant,
    deleteVariant,
    setActiveVariant,
    addStepBefore,
    addRootStep,
    addBranchForward,
  };
}
